import { apiClient, type PageData } from './client'

export type GiftProductState = 0 | 1

export interface GiftProduct {
	id: number
	name: string
	description: string
	state: GiftProductState
	price: number
	stock: number
	total_sold: number
	version: number
	created_at: string
	updated_at: string
}

export interface GiftProductListParams {
	name?: string
	state?: string
	sort?: string
	order?: string
	page?: number
	page_size?: number
}

export interface GiftProductSavePayload {
	name: string
	stock?: number
	state: GiftProductState
	description?: string
	price?: number
}

export interface GiftProductStockAdjustPayload {
	delta: number
	remark?: string
}

export interface GiftProductImportResult {
	success: number
	failed: number
}

const buildSearch = (params: GiftProductListParams) => {
	const search = new URLSearchParams()
	if (params.name) search.set('name', params.name)
	if (params.state !== undefined && params.state !== '') search.set('state', params.state)
	if (params.sort) search.set('sort', params.sort)
	if (params.order) search.set('order', params.order)
	search.set('page', String(params.page ?? 1))
	search.set('page_size', String(params.page_size ?? 30))
	return search
}

export const giftProductsApi = {
	list: async (params: GiftProductListParams) => {
		const response = await apiClient.get<PageData<GiftProduct>>(`/gift-products?${buildSearch(params)}`)
		return response.data
	},

	options: async () => {
		const response = await apiClient.get<GiftProduct[]>('/gift-products/options')
		return response.data
	},

	create: async (payload: GiftProductSavePayload) => {
		const response = await apiClient.post<GiftProduct>('/gift-products', payload)
		return response.data
	},

	update: async (id: number, payload: GiftProductSavePayload) => {
		const response = await apiClient.put<GiftProduct>(`/gift-products/${id}`, payload)
		return response.data
	},

	delete: async (id: number) => {
		await apiClient.delete(`/gift-products/${id}`)
	},

	batchDelete: async (selected: number[]) => {
		await apiClient.post('/gift-products/batch-delete', { selected })
	},

	batchUp: async (selected: number[]) => {
		await apiClient.post('/gift-products/batch-up', { selected })
	},

	batchDown: async (selected: number[]) => {
		await apiClient.post('/gift-products/batch-down', { selected })
	},

	adjustStock: async (id: number, payload: GiftProductStockAdjustPayload) => {
		const response = await apiClient.post<GiftProduct>(`/gift-products/${id}/stock-adjust`, payload)
		return response.data
	},

	template: async () => {
		await apiClient.download('/gift-products/template', undefined, { method: 'GET' })
	},

	import: async (file: File) => {
		const formData = new FormData()
		formData.append('excel', file)
		const response = await apiClient.upload<GiftProductImportResult>('/gift-products/import', formData)
		return response.data
	},
}
